/**
 * @fileoverview English translations.
 * @summary English translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/en
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
export const en = {
  // Common
  common: {
    loading: 'Loading...',
    processing: 'Processing',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    close: 'Close',
    backHome: 'Back to home',
    creating: 'Creating...',
    refresh: 'Refresh',
  },
  // Error codes
  // errors: {
  //   generic: 'An error occurred',
  //   VALIDATION_ERROR: 'Validation error',
  //   CREATE_EVENT_ERROR: 'Error creating event',
  //   UPDATE_EVENT_ERROR: 'Error updating event',
  //   EVENT_NOT_FOUND: 'Event not found',
  //   VENUE_NOT_FOUND: 'Venue not found',
  //   // Validation errors by field and Zod code
  //   validation: {
  //     name: {
  //       too_small: 'Event name is required',
  //       required: 'Event name is required',
  //       invalid_type: 'Event name is invalid',
  //     },
  //     date: {
  //       required: 'Date is required',
  //       invalid_type: 'Date is invalid',
  //       invalid_date: 'Date is invalid',
  //       invalid_string: 'Date format is invalid',
  //     },
  //     description: {
  //       invalid_type: 'Description is invalid',
  //     },
  //     venueId: {
  //       invalid_type: 'Venue is invalid',
  //     },
  //   },
  // },
  // Auth
  auth: {
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm password',
    forgotPassword: 'Forgot password?',
    rememberMe: 'Remember me',
    loginButton: 'Sign in',
    registerButton: 'Sign up',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    role: 'Role',
    organizer: 'Organizer',
    singer: 'Singer',
    firstName: 'First name',
    lastName: 'Last name',
    pseudo: 'Pseudo',
    organization: 'Organization',
    sendVerificationEmail: 'Send verification email',
    emailVerificationSent: 'Verification email sent',
    emailVerificationInstructions: 'A verification email has been sent to {{email}}. Click the link in the email to complete your registration.',
    backToLogin: 'Back to login',
    optional: 'optional',
    registerOrganizerTitle: 'Organizer Registration',
    registerSingerTitle: 'Singer Registration',
    registerOrganizerDescription: 'Create your account to organize karaoke events',
    registerSingerDescription: 'Create your account to participate in karaoke events',
    setPassword: 'Create password',
    setPasswordTitle: 'Complete registration',
    setPasswordDescription: 'Create your password to complete your registration',
    createPassword: 'Create my password',
    passwordRequirements: 'Minimum 12 characters, with uppercase, lowercase, number and special character',
    forgotPasswordDescription: 'Enter your email address and we will send you a link to reset your password.',
    sendResetLink: 'Send reset link',
    resetPasswordEmailSent: 'Password reset email sent',
    resetPasswordInstructions: 'A password reset email has been sent to {{email}}. Click the link in the email to reset your password.',
    resetPassword: 'Reset password',
    resetPasswordDescription: 'Create a new password for your account',
    resetPasswordButton: 'Reset my password',
    passwordResetSuccess: 'Password reset',
    passwordResetSuccessDescription: 'Your password has been reset successfully. You will be redirected to the login page.',
    resetLinkExpired: 'Reset link expired',
    resetLinkExpiredDescription: 'This reset link has expired (valid for 1 hour). Please request a new link.',
    requestNewResetLink: 'Request a new reset link',
    rememberPassword: 'Remember your password?',
  },
  // Organizer menu
  organizerMenu: {
    menu: 'Organizer menu',
    dashboard: 'Dashboard',
    venues: 'Venues',
    settings: 'Settings',
    dataPrivacy: 'Personal Data',
    subscriptions: 'Pricing',
    about: 'About',
    user: 'User',
  },
  // Errors
  errors: {
    genericMessage: 'An error occurred',
    required: 'This field is required',
    invalidEmail: 'Invalid email',
    passwordTooShort: 'Password must be at least 12 characters long',
    passwordRequirements: 'Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character',
    passwordsDoNotMatch: 'Passwords do not match',
    loginFailed: 'Login failed',
    registerFailed: 'Registration failed',
    resetPasswordFailed: 'Password reset failed',
    invalidToken: 'Invalid or expired token',
    networkError: 'Network error',
    unauthorized: 'Unauthorized',
    forbidden: 'Access denied',
    notFound: {
      title: 'Page not found',
      description: 'The page you are looking for does not exist or has been moved.',
      explore: 'Explore the app',
    },
    server: {
      title: 'Server error',
      description: 'An unexpected error occurred. Please try again later.',
    },
    generic: {
      title: 'Oops, something went wrong',
      description: 'An unexpected error occurred. Please try again.',
    },
    serverError: 'Server error',
    // Error codes from backend
    VALIDATION_ERROR: 'Validation error',
    CREATE_EVENT_ERROR: 'Error creating event',
    UPDATE_EVENT_ERROR: 'Error updating event',
    EVENT_NOT_FOUND: 'Event not found',
    VENUE_NOT_FOUND: 'Venue not found',
    QUOTA_EXCEEDED_CREATE: 'Monthly quota reached for event creation',
    QUOTA_EXCEEDED_DATE_CHANGE: 'Plan {{planName}} quota reached for this month: Maximum {{limit}} event(s) per month. Your quota will reset at the end of your billing period.',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: 'Events included in your subscription plan cannot be converted to a single event',
    EVENT_DATE_NOT_REACHED: 'Cannot activate event before its scheduled date',
    EVENT_DATE_PAST: 'The event date cannot be in the past',
    EVENT_DATE_AFTER_EXPIRATION: 'The event date cannot be after the expiration date of your unique event',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: 'Event name is required',
        required: 'Event name is required',
        invalid_type: 'Event name is invalid',
      },
      date: {
        required: 'Date is required',
        invalid_type: 'Date is invalid',
        invalid_date: 'Date is invalid',
        invalid_string: 'Date format is invalid',
      },
      description: {
        invalid_type: 'Description is invalid',
      },
      venueId: {
        invalid_type: 'Venue is invalid',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: 'Choose your language',
    heroTitle: 'Your karaoke evenings, simplified.',
    heroSubtitle: 'Create your event in 2 minutes. Your guests scan a QR code, add their songs — you keep control of the evening.',
    ctaPrimary: 'Create my first karaoke — free',
    ctaOrganize: 'I want to organize',
    ctaOrganizeDesc: 'Create events, manage the playlist, track performances.',
    ctaSing: 'I want to sing',
    ctaSingDesc: 'Join an event via QR code and add your songs.',
    alreadyAccount: 'Already have an account?',
    login: 'Log in',
    whyTitle: 'Why KaraoNow?',
    benefit1Title: 'One QR code, zero paper',
    benefit1Desc: 'Your guests scan, choose their songs from their phone. No more lost lists.',
    benefit2Title: 'Live playlist',
    benefit2Desc: 'Running order visible to everyone. You move through the songs without the hassle.',
    benefit3Title: 'Free to start',
    benefit3Desc: 'Up to 4 free events. Go unlimited whenever you want.',
    twoWaysTitle: 'Two ways to enjoy KaraoNow',
    learnMore: 'Learn more',
  },
  // Home page
  home: {
    title: 'Playlist management for Karaoke events',
    wantToSing: 'I want to sing',
    wantToSingDescription: 'Join events and add your songs',
    wantToOrganize: 'I want to organize',
    wantToOrganizeDescription: 'Create and manage your karaoke events',
    alreadyHaveAccount: 'Already have an account?',
  },
  // Dashboard
  dashboard: {
    title: 'Organizer Dashboard',
    welcome: 'Welcome',
    events: 'Events',
    createEvent: 'Create event',
    noEvents: 'No events',
    noEventsDescription: 'Start by creating your first karaoke event',
    filterAll: 'All',
    filterDraft: 'Drafts',
    filterActive: 'Active',
    filterCompleted: 'Completed',
    filterCancelled: 'Cancelled',
    loadingEvents: 'Loading events...',
    errorLoadingEvents: 'Error loading events',
    noEventsMatchingFilter: 'No events match this filter',
    tryDifferentFilter: 'Try selecting a different filter',
  },
  // Events
  events: {
    name: 'Event',
    events: 'Events',
    song: 'Song',
    songs: 'Songs',
    description: 'Description',
    date: 'Date',
    time: 'Time',
    venue: 'Venue',
    selectVenue: 'Select a venue',
    namePlaceholder: 'My Karaoke Night',
    descriptionPlaceholder: 'Optional description...',
    createTitle: 'Create Event',
    createSubtitle: 'Create a new karaoke event',
    createButton: 'Create Event',
    createEvent: 'Create Event',
    createSuccess: 'Event created successfully',
    createError: 'Error creating event',
    noVenues: 'No venues available. Please create a venue first.',
    createVenue: 'Create Venue',
    status: 'Status',
    statusDraft: 'Draft',
    statusActive: 'Active',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    statusHidden: 'Hidden',
    createdAt: 'Created at',
    updatedAt: 'Updated at',
    view: 'View',
    open: 'Open',
    edit: 'Edit',
    delete: 'Delete',
    qrCode: 'QR Code',
    deleteConfirmTitle: 'Delete Event',
    deleteConfirm: 'Are you sure you want to delete the event "{{name}}" ?',
    deleteSuccess: 'Event deleted successfully',
    deleteError: 'Error deleting event',
    managementTitle: 'Event Management',
    editEvent: 'Edit Event',
    updateSuccess: 'Event updated successfully',
    updateError: 'Error updating event',
    showQRCode: 'Show QR Code',
    hideQRCode: 'Hide QR Code',
    qrCodeTitle: 'Event QR Code',
    qrCodeDescription: 'Share this code with your participants so they can join the event',
    copyQRCode: 'Copy Link',
    qrCodeCopied: 'Link copied to clipboard',
    shareQRCode: 'Share',
    queue: 'Queue',
    singers: 'Singers',
    noQueue: 'No songs in the queue',
    noSingers: 'No singers registered',
    guest: 'Guest',
    singer: 'Singer',
    songsCount_one: '{{count}} song',
    songsCount_other: '{{count}} songs',
    deleteQueueItem: 'Delete song',
    deleteQueueItemConfirm: 'Are you sure you want to remove this song from the queue?',
    deleteSinger: 'Remove singer',
    deleteSingerConfirm: 'Are you sure you want to remove this singer? All their songs in the queue will also be removed.',
    singerDeleteError: 'Error removing singer',
    moveUp: 'Move up',
    moveDown: 'Move down',
    dragToReorder: 'Drag to reorder',
    setCurrent: 'Set as current',
    markCompleted: 'Mark as completed',
    cancel: 'Cancel',
    queueUpdateError: 'Error updating the queue',
    queueDeleteError: 'Error deleting the song',
    queueStatusPending: 'Pending',
    queueStatusCurrent: 'Current',
    queueStatusCompleted: 'Completed',
    queueStatusCancelled: 'Cancelled',
    qrCodeUrl: 'Event URL',
    qrCodeShareText: 'Join me for this karaoke event: {{eventName}}!',
    qrCodePrintTitle: 'QR Code - KaraoNow',
    qrCodePrintInstructions: 'Scan this QR code to access the event and add your songs to the queue.',
    qrCodePrint: 'Print',
    qrCodeShare: 'Share',
    qrCodeDownload: 'Download',
    startEvent: 'Start the event',
    start: 'Start',
    completeEvent: 'Complete the event',
    complete: 'Complete',
    cancelEvent: 'Cancel the event',
    cancelConfirmTitle: 'Confirm cancellation',
    cancelConfirmMessage: 'Are you sure you want to cancel this event? This action is final.',
    cancelConfirmOneShotTitle: 'Confirm cancellation - One-time event',
    cancelConfirmOneShotMessage: '⚠️ Warning: This one-time event was purchased. Cancellation is final and no refund will be issued if the event has not been used. Are you sure you want to cancel this event?',
    cancelConfirmButton: 'Yes, cancel',
    cancelCancelButton: 'No, keep the event',
    deleteEvent: 'Delete the event',
    deleteEventConfirm: 'Are you sure you want to delete this event? This action is irreversible.',
    hide: 'Hide',
    hideConfirmTitle: 'Hide event',
    hideConfirm: 'Hide event "{{name}}" ? It will no longer be visible in your main list, but will still count in your monthly quota.',
    hideSuccess: 'Event hidden successfully',
    hideError: 'Error hiding event',
    hidden: 'Hidden',
    showHidden: 'Hidden',
    hiddenEventTitle: 'Hidden event',
    hiddenEventMessage: 'This event has been hidden and is no longer accessible. It still counts in your monthly quota.',
    hiddenEventTooltip: 'This event is hidden and cannot be viewed',
    statusUpdated: 'Event status updated',
    backToEvent: 'Back to Event',
    // Quota exceeded for date change
    quotaExceededDateChange: 'Monthly quota reached for this date',
    quotaExceededDateChangeTitle: 'Monthly quota reached',
    quotaExceededDateChangeExplanation: 'You cannot change the date of event "{{eventName}}" as it would exceed your monthly event quota.',
    quotaExceededDateChangeOptions: 'You can either keep the original date or convert this event to "Single Event" to remove it from your monthly quota.',
    quotaExceededDetails: 'You have {{current}} event(s) out of {{limit}} for this period.',
    upgradeToOneShotOption: 'Option: Upgrade to Single Event',
    upgradeToOneShotDescription: 'By converting this event to "Single Event", it will no longer count in your monthly quota and you will be able to change its date freely.',
    upgradeToOneShotNow: '🚀 Upgrade to Single Event',
    keepOriginalDate: 'Keep original date',
    multipleEventsPremiumSuggestion: 'Multiple events? The Premium plan might be more cost-effective.',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: 'Monthly quota reached',
    quotaExceededCreateMessage: 'You have reached the limit of your {{planName}}. You cannot create more events for this period.',
    quotaExceededCreateExplanation: 'Your monthly event quota is reached. You can either wait for the end of your billing period or upgrade to a higher plan.',
    quotaExceededCreateOptions: 'You can also create a single event that will not count in your monthly quota.',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: 'Quota reached',
    quotaExceededCreateMessageFree: 'You have reached the limit of your {{planName}}. You cannot create more events.',
    quotaExceededCreateExplanationFree: 'You have reached the event limit of your free plan. You can upgrade to a higher plan to create more events.',
    quotaExceededCreateOptionsFree: 'You can also create a single event that will not count in your quota.',
    quotaExceededDetailsFree: 'You have {{current}} event(s) out of {{limit}}.',
    createOneShotDescriptionFree: 'Create a single event that will not count in your quota.',
    upgradePlanDescriptionFree: 'Upgrade to a higher plan to increase your event quota.',
    createOneShotOption: 'Option: Create a Single Event',
    createOneShotDescription: 'Create a single event that will not count in your monthly quota.',
    createOneShotNow: '🚀 Create a Single Event',
    upgradePlanOption: 'Option: Upgrade to Higher Plan',
    upgradePlanDescription: 'Upgrade to a higher plan to increase your monthly event quota.',
    upgradePlanNow: 'Upgrade to higher plan',
    quotaResetInfo: 'Your quota will reset at the end of your billing period.',
    // Generic quota exceeded message
    quotaExceeded: 'Monthly quota reached for this date',
    // Plan subscription badge
    planSubscription: 'Included in plan',
    planSubscriptionTooltip: 'Event included in your subscription',
  },
  // Venues
  venues: {
    title: 'Venue Management',
    subtitle: 'Create and manage your karaoke venues',
    venuesList: 'Venues List',
    addVenue: 'Add Venue',
    editVenue: 'Edit Venue',
    noVenues: 'No venues registered',
    noVenuesDescription: 'Start by creating your first venue',
    eventName: 'Event name',
    eventNamePlaceholder: 'Karaoke by {{firstName}}',
    name: 'Venue Name',
    namePlaceholder: 'Ex: Le Chat Noir Bar',
    address: 'Address',
    addressPlaceholder: 'Ex: 123 Music Street',
    city: 'City',
    cityPlaceholder: 'Ex: Paris',
    country: 'Country',
    countryPlaceholder: 'Ex: France',
    googleMapsUrl: 'Google Maps Link',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: 'View on Google Maps',
    addSuccess: 'Venue created successfully',
    addError: 'Error creating venue',
    updateSuccess: 'Venue updated successfully',
    updateError: 'Error updating venue',
    deleteSuccess: 'Venue deleted successfully',
    deleteError: 'Error deleting venue',
    confirmDelete: 'Are you sure you want to delete the venue "{{name}}" ?',
    confirmDeleteTitle: 'Delete Venue',
    cancel: 'Cancel',
    add: 'Add',
    edit: 'Edit',
    save: 'Save',
    deleting: 'Deleting...',
    saving: 'Saving...',
    generateMapsUrl: 'Generate Google Maps link',
    generateMapsUrlError: 'Please fill in at least the venue name or city to generate the link',
    mapsModal: {
      title: 'Select location on Google Maps',
      description: 'Use Google Maps to find and precisely adjust the location of your venue.',
      instructions: 'Click "Open in Google Maps" below, adjust the point on the map if needed, then copy the link from your browser address bar and paste it in the field below.',
      urlLabel: 'Google Maps link',
      openInMaps: 'Open in Google Maps',
      confirm: 'Confirm',
      clickToOpen: 'Click to open Google Maps',
      clickDescription: 'A new window will open with Google Maps so you can adjust the location',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: 'Dashboard',
    dashboardSubtitle: 'View your events and songs',
    myEvents: 'My events',
    mySongs: 'My songs',
    allEvents: 'All',
    activeEvents: 'Active',
    pastEvents: 'Past',
    inactiveEvents: 'Inactive',
    sortByDate: 'Sort by date',
    sortByOrganizer: 'Sort by organizer',
    sortBySongs: 'Sort by number of songs',
    noEvents: 'You haven\'t participated in any events yet.',
    noSongs: 'You haven\'t added any songs yet.',
    eventName: 'Event name',
    organizer: 'Organizer',
    date: 'Date',
    songsAdded: 'Songs added',
    songsSung: 'Songs sung',
    status: 'Status',
    status_active: 'Active',
    status_completed: 'Completed',
    status_cancelled: 'Cancelled',
    status_draft: 'Draft',
    title: 'Title',
    artist: 'Artist',
    event: 'Event',
    addedDate: 'Added date',
    totalEvents: 'Total events',
    totalSongs: 'Total songs',
    uniqueOrganizers: 'Unique organizers',
    // Settings
    settings: 'Settings',
    settingsSubtitle: 'Manage your profile and data',
    menu: 'Singer menu',
    profile: 'Profile',
    changePassword: 'Change password',
    changePasswordDescription: 'We will send you a link to reset your password by email.',
    sendResetLink: 'Send reset link',
    passwordResetLinkSent: 'A password reset link has been sent to your email address.',
    accountData: 'Account data',
    editProfile: 'Edit my profile',
    saveChanges: 'Save changes',
    profileUpdated: 'Profile updated successfully',
    changePasswordTitle: 'Change my password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    passwordRequirements: 'Password must contain at least 12 characters, one uppercase, one lowercase, one digit and one special character.',
    passwordChanged: 'Password changed successfully',
    passwordTooShort: 'Password must contain at least 12 characters',
    passwordsNotMatch: 'Passwords do not match',
    currentPasswordRequired: 'Current password is required',
    // Password validation errors
    passwordMinLength: 'Password must contain at least 12 characters',
    passwordNoUppercase: 'Password must contain at least one uppercase letter',
    passwordNoLowercase: 'Password must contain at least one lowercase letter',
    passwordNoDigit: 'Password must contain at least one digit',
    passwordNoSpecialChar: 'Password must contain at least one special character',
    exportData: 'Export my data',
    exportDataDescription: 'Download a copy of all your personal data in JSON format.',
    downloadData: 'Download my data',
    dataExported: 'Data exported successfully',
    dangerZone: 'Danger zone',
    deleteAccountDescription: 'Deleting your account is irreversible. All your data will be permanently deleted.',
    deleteAccount: 'Delete my account',
    deleteAccountConfirm: 'Delete',
    confirmDeleteAccount: 'Are you sure you want to delete your account? This action is irreversible.',
    // QR Code Landing
    qrCodeMissing: 'QR code token missing',
    qrCodeError: 'Error validating QR code',
    eventNotFound: 'Event not found or invalid token',
    organizerNotAllowed: 'You are an organizer. Use your dashboard to manage this event.',
    accessDenied: 'Access denied',
    validatingQRCode: 'Validating QR code...',
    chooseAccessMethod: 'Choose how to join the event',
    continueAsGuest: 'Continue as guest',
    orWithAccount: 'or with an account',
    enterPseudo: 'Enter your nickname to join the event',
    joinEvent: 'Join event',
    joining: 'Joining...',
    sessionError: 'Error creating session',
    joinError: 'Error joining the event',
    sessionExpired: 'Your session has expired. Please scan the event QR code again.',
    guestMode: 'Guest mode',
    leaveEvent: 'Leave event',
    // Singer Event Page
    eventNotActive: 'This event is not active yet. Come back later!',
    eventNotStarted: 'This event hasn\'t started yet. It will be available once the organizer launches the karaoke!',
    eventNotYetAvailable: 'This event is not yet available. Please wait until the scheduled date.',
    eventEnded: 'This event is over. Votes are no longer accepted.',
    eventCancelled: 'This event has been cancelled. Votes are not available.',
    searchSong: 'Search for a song',
    searchResults: 'Search results',
    advancedSearch: 'Advanced search',
    advancedSearchToggle: 'Advanced search',
    simpleSearch: 'Simple search',
    searchPlaceholder: 'Title or artist...',
    titlePlaceholder: 'Title...',
    artistPlaceholder: 'Artist...',
    noResults: 'No results found',
    addCustomSong: 'Add this title',
    addCustomSongDescription: 'This title will be saved to the database and added to your queue.',
    addAndQueue: 'Add and queue',
    localResults: '{{count}} result(s) found in our database',
    musicbrainzResults: '{{count}} MusicBrainz result(s)',
    moreResults: 'More results',
    addError: 'Error adding to queue',
    queueLimitReached: 'Limit reached: the organizer of this event is using the free plan which is limited to {{limit}} song(s) maximum in the queue. The queue currently contains {{currentCount}} song(s). Please contact the organizer if you wish to add more songs.',
    songAdded: '"{{title}}" added to queue!',
    myQueue: 'My songs',
    noSongsInQueue: 'You haven\'t added any songs yet',
    fullQueue: 'Queue',
    queueEmpty: 'The queue is empty',
    you: 'You',
    unknown: 'Unknown',
    // Queue statuses
    statusPending: 'Pending',
    statusCurrent: 'Current',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
  },
  // Singer menu
  singerMenu: {
    about: 'About',
    dataPrivacy: 'Personal Data',
  },
  // Subscription and Plans
  subscription: {
    title: 'Plans and pricing',
    subtitle: 'Choose the plan that fits your needs',
    currentPlan: 'Current plan',
    planFree: 'Free Plan',
    planOneShot: 'Single Event',
    planPro: 'Pro Plan',
    planPremium: 'Premium Plan',
    planUltimate: 'Ultimate Plan',
    planFreeDescription: 'Perfect to get started',
    planOneShotDescription: 'For one event',
    planProDescription: 'For regular organizers',
    planPremiumDescription: 'For enthusiasts',
    planUltimateDescription: 'For professionals',
    priceFreeFree: 'Free',
    priceOneShotPer: '{{price}}€ / event',
    priceProPer: '{{price}}€ / month',
    pricePremiumPer: '{{price}}€ / month',
    priceUltimatePer: '{{price}}€ / month',
    maxEvents: 'Maximum events',
    maxSongsPerEvent: 'Songs per event',
    unlimited: 'Unlimited',
    features: 'Features',
    currentPlanBadge: 'Current plan',
    changePlan: 'Change plan',
    upgradePlan: 'Upgrade to premium',
    upgradePlanButton: 'Upgrade to higher plan',
    selectPlan: 'Select this plan',
    monthlyQuota: 'Monthly quota',
    freeQuota: 'Free quota',
    eventsUsed: '{{current}} / {{limit}} events',
    billingPeriod: 'Period: {{start}} - {{end}}',
    quotaReached: 'Monthly quota reached',
    quotaReachedMessage: 'You have reached your event limit for this period. The quota will reset at the end of your billing period.',
    quotaWarning: 'Monthly quota almost reached',
    quotaWarningMessage: 'You have {{remaining}} event(s) remaining for this period.',
    quotaInfo: 'You have {{remaining}} event(s) remaining for this period.',
    learnMore: 'Learn more',
    contactSupport: 'Contact support',
    freePlanNote: 'Free plan is limited to {{maxEvents}} events (all statuses, excluding Single Event) and {{maxSongsPerEvent}} songs per event.',
    oneShotPlanNote: 'Unlimited number of songs in the event\'s playlist. Valid for 15 days.',
    proPlanNote: '{{maxEvents}} events per month (rolling period). Unlimited songs.',
    premiumPlanNote: '{{maxEvents}} events per month (rolling period). Unlimited songs.',
    ultimatePlanNote: 'Unlimited events and songs.',
    noEngagement: 'No commitment.',
    convertEvent: 'Convert an event',
    createEvent: 'Create an event',
    chooseOption: 'Choose an option:',
    convertDescription: 'Select a free event and convert it to a Single Event plan.',
    createDescription: 'Create a new event with the Single Event plan.',
    noFreeEventsToConvert: 'You have no free event available to convert.',
    selectFreeEvent: 'Select a FREE event to convert:',
    convertThisEvent: 'Convert this event',
    createNewOneShot: 'Create a Single Event',
    newOneShotEventName: 'New Single Event',
    createEventError: 'Error creating event',
    createOneShotEventTitle: 'Create a Single Event',
    proceedToPayment: 'Proceed to payment',
    // Stripe checkout
    checkoutError: 'An error occurred during payment',
    paymentSuccessful: 'Payment successful!',
    paymentProcessing: 'Your payment is being processed. You will be redirected shortly.',
    redirectingIn5: 'Automatic redirection in 5 seconds...',
    backToSubscription: 'Back to plans',
    successTitle: 'Payment confirmed',
    successSubtitle: 'Thank you for your purchase',
    paymentCancelled: 'Payment cancelled',
    checkoutCancelledMessage: 'You cancelled the payment. You can try again anytime.',
    cancelledTitle: 'Payment cancelled',
    cancelledSubtitle: 'Return to plans to try again',
    whatHappensNext: 'What happens next?',
    webhooksProcessing: 'We are processing your payment',
    subscriptionActive: 'Your subscription will be active soon',
    confirmationEmail: 'You will receive a confirmation email',
    // Subscription cancellation
    cancelConfirm: 'Are you sure you want to cancel your subscription?',
    cancelDetails: 'You will keep access to your Premium plan until the end of your current billing period. After that date, you will be downgraded to the Free plan.',
    cancelSuccess: 'Your subscription has been cancelled successfully.',
    cancelPlan: 'Cancel',
    cancellationInProgress: 'Your subscription cancellation is already in progress. You will be switched to the Free plan at the end of your billing period.',
    cancellationInProgressTitle: 'Cancellation in Progress',
    cancellationInProgressMessage: 'Your subscription cancellation is already in progress. You will be switched to the Free plan at the end of your current billing period. No further action is required.',
    accessUntil: 'You will have access until {{date}}.',
    cancelError: 'An error occurred while cancelling your subscription',
    cancelModalTitle: 'Cancel Subscription',
    cancelModalMessage: 'You will retain access to your Premium plan until the end of your current billing period. After that date, you will automatically be switched to the Free plan.',
    cancelModalConfirm: 'Yes, cancel my subscription',
    endOfBillingPeriod: 'end of billing period',
    // Single event upgrade
    upgradeToOneShot: 'Upgrade to Single Event',
    upgradeOneShotConfirm: 'You are about to upgrade the event "{{eventName}}" to Single Event.',
    oneShotDescription: 'This is a one-time payment for unlimited songs in this specific event.',
    price: 'Price',
    oneTimePayment: 'One-time payment',
    whatYouGet: 'What you\'ll get:',
    unlimitedSongs: 'Unlimited songs for this event',
    noLimits: 'No queue limits',
    oneShotValidity: 'Valid until event completion',
    upgradeNow: 'Upgrade Now',
    upgradeError: 'Error upgrading event',
    updateSuccess: 'Subscription updated successfully to {{plan}} plan',
    changePlanTitle: 'Change Plan',
    changePlanSubtitle: 'You are about to modify your subscription',
    newPlan: 'New plan',
    perMonth: 'month',
    upgradeCost: 'Additional cost: +{{amount}}€ / month',
    downgradeCredit: 'Estimated credit: -{{amount}}€ / month',
    immediateChange: 'Immediate change',
    immediateChangeDescription: 'The plan change will be effective immediately. Prorated billing will be calculated automatically by Stripe.',
    confirmChange: 'Confirm change',
    oneShot: 'Single Event',
    oneShotTooltip: 'Single event subscription',
    expiresLabel: 'Expires on',
    oneShotExpirationTitle: 'Your single event expiration',
    oneShotDaysRemaining: '{{count}} day remaining',
    oneShotDaysRemaining_plural: '{{count}} days remaining',
    oneShotExpiresOn: 'Expires on {{date}}',
    oneShotExpirationWarning: '⚠️ Your event will revert to the free plan after this date. Make sure to use it before expiration!',
    oneShotValidityInfo: 'This purchase is valid for {{days}} days from the purchase date. Use it before expiration!',
    upgradeToUnlimited: '🚀 Go Unlimited',
    upgradeToUnlimitedNow: '🚀 Go Unlimited Now',
    upgradeSingleEventConfirm: 'You are about to upgrade the event "{{eventName}}" to unlimited.',
    singleEventDescription: 'This is a one-time payment for unlimited songs in this specific event.',
    singleEventPlan: 'Single Event',
    multiplePremiumSuggestion: 'Multiple events? The Premium plan might be more cost-effective.',
    comparePlans: 'Compare plans →',
    premium: 'PREMIUM',
  },
  // Plan alerts
  planAlert: {
    limitReached: 'Plan limit reached',
    songsLimitReached: 'You have reached the free plan limit ({{limit}} song(s)). To add more songs, subscribe to a paid plan.',
    eventsLimitReached: 'You have reached the free plan limit ({{limit}} event(s)). To create more events, subscribe to a paid plan.',
    upgrade: 'Subscribe',
    dismissible: true,
  },
  pwa: {
    install: 'Install',
    installApp: 'Install app',
    installPrompt: 'Install KaraoNow for quick access!',
    installed: 'App installed',
    offline: 'You are offline',
    offlineMessage: 'Some features may be limited.',
    updateAvailable: 'Update available',
    updateNow: 'Update now',
  },
  // About
  about: {
    title: 'About KaraoNow',
    subtitle: 'Discover the app that transforms your karaoke events',
    introduction: 'What is KaraoNow?',
    introductionText: 'KaraoNow is an innovative SaaS platform designed to manage your karaoke events seamlessly and professionally. Whether you are an event organizer or passionate singer, KaraoNow provides you with the tools needed to create an unforgettable karaoke experience.',
    missionText: 'Our mission is to simplify karaoke playlist management and create an authentic connection between organizers and singers, making every event memorable and well-organized.',
    features: 'Key features',
    feature1Title: 'Event Management',
    feature1Desc: 'Easily create and manage your karaoke events with all necessary details.',
    feature2Title: 'Interactive Participation',
    feature2Desc: 'Allow singers to join your events via QR code and vote for their favorite songs.',
    feature3Title: 'Real-time Playlist',
    feature3Desc: 'View and manage the song queue live with instant updates.',
    feature4Title: 'Detailed Statistics',
    feature4Desc: 'Analyze your event performance with charts and real-time metrics.',
    feature5Title: 'Enhanced Security',
    feature5Desc: 'Your data is protected by high security standards and robust cloud infrastructure.',
    feature6Title: 'Multilingual Support',
    feature6Desc: 'KaraoNow supports 8 languages to reach a global audience.',
    technology: 'Technology stack',
    frontend: 'Frontend',
    backend: 'Backend',
    version: 'Current version',
    versionNote: 'Updated regularly with new features',
    visitWebsite: 'Official website',
    support: 'Support',
    supportNote: 'Feel free to contact us with any questions',
    copyright: 'All rights reserved.',
    privacyNotice: 'Check our legal pages to learn more about data protection.',
    tryForFree: 'Start your free trial',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: 'Dashboard',
    dashboardSubtitle: 'Manage your karaoke events',
    menu: 'Organizer menu',
    // Settings
    settings: 'Settings',
    settingsSubtitle: 'Manage your profile and settings',
    editProfile: 'Edit my profile',
    saveChanges: 'Save changes',
    profileUpdated: 'Profile updated successfully',
    emailChangeConfirmTitle: 'Email address modification',
    emailChangeConfirmMessage: 'A confirmation email will be sent to your new email address. Your new email will only be effective after you validate the link in that email. Your old email remains active until validation.',
    changePassword: 'Change password',
    changePasswordDescription: 'We will send you a link to reset your password by email.',
    sendResetLink: 'Send reset link',
    passwordResetLinkSent: 'A password reset link has been sent to your email.',
    accountData: 'Account data',
    exportData: 'Export my data',
    exportDataDescription: 'Download a copy of all your personal data as JSON.',
    downloadData: 'Download my data',
    dataExported: 'Data exported successfully',
    dangerZone: 'Danger zone',
    deleteAccountDescription: 'Deleting your account is irreversible. All your data will be permanently deleted.',
    deleteAccount: 'Delete my account',
    deleteAccountConfirm: 'Delete',
    confirmDeleteAccount: 'Are you sure you want to delete your account? This action is irreversible.',
    // Plan section
    planSection: 'My Subscription',
    planSectionDescription: 'View your current plan, limits and upgrade options',
    renewDate: 'Renewal on ',
    viewPlanDetails: 'View plan details',
    eventsLimit: 'Events created (excluding Unique Event)',
    subscriptionStatus: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusScheduledEnd: 'Scheduled End',
    subscriptionExpires: 'Expires on',
    subscriptionRenewsOn: 'Renews on',
    cancellationConfirmed: 'Cancellation Confirmed!',
    continueAccessUntil: 'You continue to enjoy your Premium plan until {{date}}',
    autoDowngradeToFree: 'You will automatically switch to the Free plan after this date.',
    accessUntilEndOfPeriod: 'You retain access until the end of your current billing period.',
    planFeatures: 'Plan features',
    featureEvents: 'Events per month',
    featureSongsPerEvent: 'Songs per event',
    unlimited: 'Unlimited',
    upgradePlan: 'Upgrade my plan',
    managePlan: 'Manage my subscription',
  },
  // Email change
  email: {
    changeEmail: 'Change my email address',
    changeEmailDescription: 'To change your email address, you must first confirm the new address. A confirmation email will be sent to the new address.',
    currentEmail: 'Current email',
    newEmail: 'New email address',
    newEmailAddress: 'New address',
    requestEmailChange: 'Request email change',
    emailRequired: 'Email address is required',
    emailUnchanged: 'The new email address must be different from the current one',
    emailChangeConfirmationSent: 'A confirmation email has been sent to your new email address.',
    emailChangeInfo: 'Your old email remains active until the new address is validated.',
    verifying: 'Verifying',
    verifyingDescription: 'Verifying your email address...',
    success: 'Email confirmed',
    confirmationSuccess: 'Your email address has been successfully changed!',
    confirmationFailed: 'Confirmation failed',
    linkExpired: 'Link expired',
    tokenExpired: 'The confirmation link has expired. Please request a new one.',
    linkExpiredDescription: 'The confirmation link has expired (valid for 24 hours). Please request a new confirmation link.',
    redirecting: 'Redirecting...',
    redirectingToLogin: 'Redirecting to login in 3 seconds...',
    goToSettings: 'Go to settings',
    backToSettings: 'Back to settings',
    requestNewLink: 'Request a new link',
    missingToken: 'Confirmation token is missing.',
    validationPending: 'Validation pending',
    pendingEmailAddress: 'Awaiting validation',
  },
  // RGPD - Data Privacy
  rgpd: {
    pageTitle: 'Personal Data',
    pageSubtitle: 'Manage your data and account securely',
    exportData: 'Download My Data',
    exportDataDescription: 'Download a complete copy of your personal data in JSON format. You can use this data to import it elsewhere or simply keep it as a backup.',
    exportIncludes: 'This export includes',
    exportProfile: 'Profile and preferences',
    exportHistory: 'Event history',
    exportEvents: 'Created events',
    exportSubscription: 'Subscription information',
    downloadData: 'Download (JSON)',
    exporting: 'Downloading...',
    exportFormatJSON: 'Format: JSON (GDPR compliant)',
    dataExportedSuccess: '✅ Your data has been downloaded successfully!',
    deleteAccount: 'Delete My Account',
    deleteAccountDescription: 'This action will permanently delete your account and all associated data. This operation cannot be undone.',
    deleteWarning: '⚠️ Important Warning',
    deleteIrreversible: 'This action is permanent and irreversible',
    deleteAllData: 'All your data will be deleted',
    deleteNoRecovery: 'There will be no possibility of recovery',
    deleteRecommendation: 'We recommend downloading your data before deleting your account.',
    deleteAccountButton: 'Delete My Account',
    confirmDeletion: 'Confirm deletion',
    deleteConfirmMessage: 'Are you sure you want to delete your account? This action cannot be undone.',
    continueDelete: 'Continue to deletion',
    finalDeleteWarning: 'Final Confirmation - No Going Back',
    finalDeleteMessage: 'This is your last chance! Clicking "Permanently Delete" will irreversibly delete your account.',
    permanentlyDelete: 'Permanently Delete',
    gdprCompliance: 'GDPR Compliance',
    gdprInfo: 'Under GDPR regulations, you have the right to access, rectify, erase, and port your personal data. These tools allow you to exercise these rights.',
  },
  // Cookies consent
  cookies: {
    title: '🍪 We use cookies',
    description: 'We use cookies to improve your experience. Check our policy for more information.',
    understood: 'Got it',
    learnMore: 'Learn more',
    footerNote: 'You can check our Cookie Policy for more details.',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'Cookies Policy',
    subtitle: 'Complete information about cookies used on KaraoNow',
    overview: 'Overview',
    overviewText: 'KaraoNow uses cookies to enhance your user experience, secure your account, and understand how you use our platform. This policy explains how and why we use cookies.',
    whatAreCookies: 'What are Cookies?',
    whatAreCookiesText: 'Cookies are small text files stored on your device. They allow websites to remember information about your visits, such as your preferences and login data.',
    typesOfCookies: 'Types of Cookies',
    sessionCookies: 'Session Cookies',
    sessionCookiesText: 'These cookies expire when you close your browser. They are used to keep your session active and secure your access.',
    persistentCookies: 'Persistent Cookies',
    persistentCookiesText: 'These cookies remain stored on your device for a defined period. They allow us to recognize you on your next visits.',
    howWeUseCookies: 'How We Use Cookies',
    useAuthentication: 'Authentication and session security',
    usePreferences: 'Remembering your preferences (language, theme)',
    useSecurity: 'Protection against abuse and attacks',
    usePerformance: 'Improving platform performance',
    yourRights: 'Your Rights',
    yourRightsText: 'You have the right to control cookies. You can accept or refuse non-essential cookies via our consent banner.',
    managingCookies: 'Managing Cookies',
    managingCookiesText: 'You can manage cookies via your browser settings. Note that disabling certain cookies may affect KaraoNow functionality.',
    contact: 'Contact',
    contactText: 'For any questions about this policy, please contact us via our contact page or terms of service.',
    footer: 'Have questions? Go back home.',
  },
  // Footer
  footer: {
    legal: 'Legal notice',
    privacy: 'Privacy policy',
    cookies: 'Cookie policy',
    terms: 'Terms and Conditions',
  },
  // Legal mentions page
  legalPage: {
    title: 'Legal Notice',
    subtitle: 'Legal information about KaraoNow',
    editor: {
      title: 'Site Editor',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, Martinique',
      phone: 'Phone: +596696367741',
      email: 'Email: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: 'Publication Director',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Hosting',
    },
    dataProtection: {
      title: 'Data Protection',
      description: 'To learn more about the protection of your personal data, please read our privacy policy.',
      link: 'Read our privacy policy',
    },
  },
  // Privacy policy page
  privacyPage: {
    title: 'Privacy Policy',
    subtitle: 'How we protect your personal data',
    lastUpdated: 'Last updated: January 1, 2025',
    intro: {
      title: 'Introduction',
      description: 'KaraoNow is committed to protecting your privacy. This policy explains how we collect, use, and protect your personal data in compliance with GDPR.',
    },
    dataCollection: {
      title: 'Data Collected',
      organizers: {
        title: 'For Organizers',
        email: 'Email address',
        name: 'First name, last name and organization',
        organization: 'Organization details',
        payment: 'Payment information (via third-party processor)',
        events: 'Information about created events',
      },
      singers: {
        title: 'For Singers',
        email: 'Email address (optional)',
        pseudo: 'User pseudonym',
        name: 'First and last name (optional)',
        participation: 'Events you have participated in',
        history: 'History of songs added to the queue',
      },
    },
    purposes: {
      title: 'Use of your data',
      authentication: 'Authentication and account management',
      eventManagement: 'Event creation and management',
      songManagement: 'Management of song queues',
      communication: 'Important communications regarding your account',
      billing: 'Payment processing and invoicing',
      support: 'Customer support and technical assistance',
      preferences: 'Improving your user experience',
    },
    legalBasis: {
      title: 'Legal basis for processing',
      description: 'Processing of your data is based on your consent upon registration and the execution of the service contract between you and KaraoNow.',
    },
    retention: {
      title: 'Data retention period',
      active: 'Active data: retained as long as your account is active',
      inactive: 'Inactive data: deleted 12 months after last login',
      legal: 'Legal data: retained as long as required by law',
    },
    rights: {
      title: 'Your GDPR Rights',
      description: 'Under GDPR, you have the following rights:',
      access: 'Right to access your personal data',
      rectification: 'Right to rectification of your data',
      deletion: 'Right to be forgotten (data deletion)',
      portability: 'Right to data portability',
      opposition: 'Right to object to processing',
      limitation: 'Right to restrict processing',
      contact: 'To exercise these rights, contact us at support@karaonow.com',
    },
    security: {
      title: 'Security',
      description: 'We use appropriate security measures to protect your data against unauthorized access, modification, or disclosure.',
    },
    cookies: {
      title: 'Cookies',
      description: 'KaraoNow uses cookies to enhance your user experience. To learn more, please read our cookie policy.',
      link: 'Read our cookie policy',
    },
    contact: {
      title: 'Contact Us',
      description: 'For any questions about this policy or your personal data, please contact us:',
      email: 'support@karaonow.com',
    },
  },
  // Terms of Service page
  termsPage: {
    title: 'Terms and Conditions',
    subtitle: 'KaraoNow Terms and Conditions',
    lastUpdated: 'Last updated: January 1, 2025',
    intro: {
      title: 'Introduction',
      description: 'Welcome to KaraoNow! By using our platform, you accept these terms and conditions. Please read them carefully.',
    },
    service: {
      title: 'Service Description',
      description: 'KaraoNow is a SaaS platform for managing karaoke events. The platform offers two main interfaces:',
      organizers: {
        title: 'For Organizers',
        description: 'Organizers can create events, generate QR codes to share with singers, manage song queues, and modify singer turn order. A subscription system offers three plans: free (limited), single event, and premium (monthly subscription).',
      },
      singers: {
        title: 'For Singers',
        description: 'Singers can join events via QR code, add songs to the event queue, and view song order. Singers can participate without an account (guest mode) or with an account for advanced features.',
      },
    },
    accounts: {
      title: 'User Accounts',
      description: 'To use KaraoNow, you must create an account. You are responsible for maintaining the confidentiality of your credentials.',
      responsibility: 'You are responsible for all activities performed through your account',
      accuracy: 'You agree to provide accurate and up-to-date information',
      security: 'You agree to maintain the security of your password',
      notTransferable: 'Your account is personal and non-transferable',
    },
    subscription: {
      title: 'Subscription Plans',
      description: 'KaraoNow offers several subscription plans for organizers:',
      free: 'Free plan: Maximum 4 events (all statuses combined, excluding unique events), 15 songs per event',
      oneShot: 'Unique event: One-time payment for a specific event, unlimited songs, limited validity period',
      premium: 'Paid plans with monthly subscription: Several plans available with monthly event quotas, unlimited songs per event, automatic monthly renewal',
    },
    payment: {
      title: 'Payment Terms',
      description: 'Payment is processed via a secure third-party payment processor.',
      currency: 'Payments are made in euros (EUR)',
      noRefund: 'No refunds are given for services already provided',
      cancellation: 'You can cancel your subscription at any time',
      automaticRenewal: 'Monthly subscriptions renew automatically each month unless cancelled',
    },
    conduct: {
      title: 'User Conduct',
      description: 'You agree to use KaraoNow in a legal and ethical manner.',
      prohibited: {
        title: 'Prohibited Actions',
        illegal: 'Any illegal activity or violation of applicable laws',
        harmful: 'Harmful, offensive, or defamatory content',
        harassing: 'Harassment, intimidation, or discrimination',
        spam: 'Sending spam or other unwanted communications',
        fraud: 'Attempts at fraud or scams',
      },
    },
    organizerResponsibilities: {
      title: 'Organizer Responsibilities',
      description: 'Organizers accept the following responsibilities:',
      songSelection: 'You are responsible for the legality of song content',
      eventManagement: 'You must manage your event in accordance with these terms',
      dataManagement: 'You are responsible for managing singer data',
      copyrightCompliance: 'You agree to comply with copyright laws for songs',
    },
    singerRights: {
      title: 'Singer Rights and Responsibilities',
      description: 'Singers have the following rights and accept the following responsibilities:',
      participation: 'You can participate in events organized by others',
      songSelection: 'You can add songs to the event queue',
      noDuplication: 'You cannot add the same song multiple times',
      respect: 'You agree to respect the rules set by the organizer',
    },
    intellectualProperty: {
      title: 'Intellectual Property',
      description: 'All intellectual property rights to KaraoNow and its content (except musical songs) belong to KaraoNow. You cannot reproduce or use the content without permission.',
    },
    liability: {
      title: 'Limitation of Liability',
      description: 'KaraoNow is provided "as is". We are not liable for direct or indirect damages resulting from platform use. Our liability is limited to the maximum amount you have paid for your subscription in the last month.',
    },
    termination: {
      title: 'Termination',
      description: 'KaraoNow reserves the right to suspend or terminate your account in case of breach of these terms. You may terminate your account at any time.',
    },
    modifications: {
      title: 'Modifications to Terms',
      description: 'KaraoNow reserves the right to modify these terms at any time. Modifications take effect 30 days after notification. Your continued use of the platform after this period signifies your acceptance of the new terms.',
    },
    jurisdiction: {
      title: 'Jurisdiction',
      description: 'These terms are governed by French law. Any dispute will be subject to the competent courts in France.',
    },
    contact: {
      title: 'Contact',
      description: 'For any questions about these terms, please contact us:',
      email: 'support@karaonow.com',
    },
  },
}
